<template>
  <div class="company-register">
    <div class="background-image right-top"></div>
    <HomeHeader class="header" />
    <div class="register-form-container">
      <div class="register-title">{{ $t(`${this.i18nPrefix}.title.registerWithAjinga`)}}</div>
      <WrappedFrom
        ref="form"
        class="company-register-form"
        hideRequiredAsterisk
        :model="dataInfo"
        :formLines="formLines"
      >
      </WrappedFrom>
      <div class="button-container">
        <el-button class="register-btn" type="primary" @click="handleSave">{{ $t('login.common.btn.register.candidate') }}</el-button>
      </div>
      <div class="form-bottom">
      <div class="bottom-line">
        <el-checkbox v-model="agree" class="policy-checkbox">
          <span>{{ $t('login.common.message.policy.tip') }}</span>
          <a class="link" href="/tos/" target="_blank">{{ $t('login.common.message.policy.userAgreement') }}</a>
          <span> & </span>
          <a class="link" href="/policy/" target="_blank">{{ $t('login.common.message.policy.privacyPolicy') }}</a>
        </el-checkbox>
        <div class="line-tip"></div>
      </div>
    </div>
    </div>
    <CommonFooter class="footer"/>
  </div>
</template>

<script>
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validateEmail, validatePhone, validatePassword } from '@/utils/validator'
import HomeHeader from '@/components/layout/header/homeHeader.vue'
import CommonFooter from '@/components/layout/footer/commonFooter.vue'
import { passwordEncrypt } from '@/utils/rsa'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedFrom,
    HomeHeader,
    CommonFooter
  },
  data () {
    return {
      i18nPrefix: 'login.common',
      loading: false,
      agree: true,
      dataInfo: {
        firstName: null,
        lastName: null,
        emailAddress: null,
        mobile: null,
        password: null,
        companyName: null
      }
    }
  },
  computed: {
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const lines = {
        firstName: {
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.firstName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        lastName: {
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.lastName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        emailAddress: {
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.email`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' },
            { validator: validateEmail, trigger: 'blur' }
          ]
        },
        mobile: {
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.phone`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' },
            { validator: validatePhone, trigger: 'blur' }
          ]
        },
        password: {
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.password`),
          rules: [
            { required: true, message: requiredMessage, trigger: 'blur' },
            { validator: validatePassword, trigger: 'blur' }
          ],
          props: {
            type: 'password',
            showPassword: true
          }
        },
        companyName: {
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.companyName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        }
      }
      return lines
    }
  },
  created () {
  },
  mounted () {

  },
  methods: {
    ...mapActions('company', ['registerCompany']),
    async handleSave () {
      if (!this.agree) {
        this.$message.warning(this.$t('login.common.message.agreePolicyFirst'))
        return
      }

      const result = await this.$refs.form.submit()
      if (!result) {
        return
      }

      this.loading = true
      try {
        const { firstName, lastName, emailAddress, mobile, password, companyName } = this.dataInfo
        const params = {
          first_name: firstName,
          last_name: lastName,
          email_address: emailAddress,
          mobile,
          password: passwordEncrypt(password),
          company_name: companyName
        }
        await this.registerCompany(params)
        this.dataInfo = {
          firstName: null,
          lastName: null,
          emailAddress: null,
          mobile: null,
          password: null,
          companyName: null
        }
        const backUrl = this.$route.query.back
        if (backUrl) {
          mixedRouterPush(this.$route.query.back)
        } else {
          this.$router.push({ path: '/v-admin/client-management/company/list/' })
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.company-register {
  min-width: 1100px;
  min-height: 100vh;
  padding-bottom: 80px;
  font-size: 14px;
  line-height: 20px;
  background-color: #e8f9ff;
  color: #606266;
  box-sizing: border-box;
  position: relative;

  .background-image {
    display: block;
    position: absolute;
    z-index: 1;

    &.right-top {
      width: 100vw;
      height: calc(100vw / 180 * 47);
      background: url(~@/assets/login/right_top_bg.png) no-repeat;
      background-size: 100%;
      top: 124px;
      right: 0;
    }
  }

  .header {
    height: 124px;
    background-color: #ffffff;
    ::v-deep {
      .right-container {
        .tab-employer {
          a {
            padding-bottom: 0;
            border-bottom: none;
          }
        }
      }
    }
  }

  .register-form-container {
    position: relative;
    z-index: 10;
    background-color: #ffffff;
    width: 500px;
    margin: 50px auto 40px;
    padding: 30px 50px 30px 30px;
    box-sizing: border-box;
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, .12);

    .register-title {
      font-size: 24px;
      text-align: center;
      margin: 30px auto;
    }

    ::v-deep {
      .el-form-item {
        margin-bottom: 24px;
      }

      .el-form-item__label-wrap {
        .el-form-item__label {
          padding: 0;
        }
      }

      .el-form-item__content {
        margin-left: 0;

        .el-input__inner {
          height: 40px;
          line-height: 40px;
          border-radius: 2px;
          padding: 0 14px;

          &:focus {
            border-color: #0096cc;
          }
        }
      }
    }

    .button-container {
      padding-left: 20px;
      margin: 0 auto;

      .el-button {
        width: 100%;
        padding: 9px 0;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        text-align: center;
        border: 0;
        background-color: #0096cc;
      }
    }
    .form-bottom {
      margin-top: 20px;

      .bottom-line {
        margin-left: 20px;
        margin-bottom: 10px;

        .policy-checkbox {
          white-space: nowrap;

          .link {
            color: #0096cc;
          }

          ::v-deep {
            .el-checkbox__inner {
              width: 12px;
              height: 12px;

              &::after {
                top: 0;
                left: 3px;
              }
            }

            &.is-focus .el-checkbox__inner {
              border-color: #0096cc;
            }

            &.is-checked .el-checkbox__inner {
              background-color: #0096cc;
              border-color: #0096cc;
            }

            .el-checkbox__label {
              white-space: initial;
              word-break: break-word;
              vertical-align: text-top;
              color: #606266;
              font-size: 12px;
              line-height: 19px;
            }
          }
        }

        .register-tip {
          font-size: 12px;
          line-height: 17px;
          margin-bottom: 14px;
        }
      }
    }
  }

  .footer {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
    text-align: center;
    position: absolute;
    bottom: 40px;
    left: 0;
    right: 0;
    flex-direction: column;
    align-items: center;
    width: initial;

    ::v-deep {

      &.custom-footer {
        width: 100%;
        position: initial;
      }

      .copyright {
        color: #909399;
      }

      .navigation {
        margin-top: 5px;

        & > div {
          margin: 0 5px;
        }
      }
    }
  }
}
</style>
